export type PhoneRegionCode =
  | 'esp'
  | 'prt'
  | 'fra'
  | 'gbr'
  | 'twn'
  | 'usa'
  | 'deu'
  | 'ita'
  | 'cri';

export const PHONE_REGION_OPTIONS: ReadonlyArray<{
  code: PhoneRegionCode;
  dialPrefix: string;
  iso2: string;
}> = [
  { code: 'esp', dialPrefix: '+34', iso2: 'ES' },
  { code: 'prt', dialPrefix: '+351', iso2: 'PT' },
  { code: 'fra', dialPrefix: '+33', iso2: 'FR' },
  { code: 'gbr', dialPrefix: '+44', iso2: 'GB' },
  { code: 'deu', dialPrefix: '+49', iso2: 'DE' },
  { code: 'ita', dialPrefix: '+39', iso2: 'IT' },
  { code: 'usa', dialPrefix: '+1', iso2: 'US' },
  { code: 'cri', dialPrefix: '+506', iso2: 'CR' },
  { code: 'twn', dialPrefix: '+886', iso2: 'TW' }
] as const;

const PHONE_REGION_ALIASES: Record<string, PhoneRegionCode> = {
  // España
  ES: 'esp',
  ESP: 'esp',
  ESPAÑA: 'esp',
  ESPANA: 'esp',
  SPAIN: 'esp',

  // Portugal
  PT: 'prt',
  PRT: 'prt',
  PORTUGAL: 'prt',

  // Francia
  FR: 'fra',
  FRA: 'fra',
  FRANCE: 'fra',
  FRANCIA: 'fra',

  // Reino Unido
  GB: 'gbr',
  GBR: 'gbr',
  UK: 'gbr',
  'UNITED KINGDOM': 'gbr',
  'REINO UNIDO': 'gbr',

  // Taiwán
  TW: 'twn',
  TWN: 'twn',
  TAIWAN: 'twn',
  'TAIWAN ROC': 'twn',
  'REPUBLIC OF CHINA': 'twn',

  // Estados Unidos
  US: 'usa',
  USA: 'usa',
  'UNITED STATES': 'usa',
  'UNITED STATES OF AMERICA': 'usa',
  'ESTADOS UNIDOS': 'usa',
  'EEUU': 'usa',
  'EE UU': 'usa',

  // Alemania
  DE: 'deu',
  DEU: 'deu',
  GERMANY: 'deu',
  ALEMANIA: 'deu',
  DEUTSCHLAND: 'deu',

  // Italia
  IT: 'ita',
  ITA: 'ita',
  ITALY: 'ita',
  ITALIA: 'ita',

  // Costa Rica
  CR: 'cri',
  'COSTA RICA': 'cri'
};

function normalizeKey(value?: string | null): string {
  if (!value) return '';
  return value
    .toString()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[\._\-\/,()]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function normalizePhoneRegionCode(countryOrRegion?: string | null): PhoneRegionCode {
  const key = normalizeKey(countryOrRegion);
  if (!key) return 'esp';

  // Ya viene en el formato interno
  if (
    key === 'ESP' ||
    key === 'PRT' ||
    key === 'FRA' ||
    key === 'GBR' ||
    key === 'TWN' ||
    key === 'USA' ||
    key === 'DEU' ||
    key === 'ITA' ||
    key === 'CRI'
  ) {
    return key.toLowerCase() as PhoneRegionCode;
  }

  const mapped = PHONE_REGION_ALIASES[key];
  if (mapped) return mapped;

  // Compat: algunos flujos guardan iso2 en minúsculas o claves mixtas
  const iso2Candidate = key.slice(0, 2);
  if (PHONE_REGION_ALIASES[iso2Candidate]) return PHONE_REGION_ALIASES[iso2Candidate];

  return 'esp';
}

export function isValidLocalPhoneForRegion(region: string | null | undefined, rawPhone: string | null | undefined): boolean {
  const digits = String(rawPhone ?? '').replace(/\D/g, '');
  if (!digits) return true;

  const normalized = normalizePhoneRegionCode(region);

  switch (normalized) {
    case 'esp':
    case 'prt':
      return /^\d{9}$/.test(digits);
    case 'usa':
      return /^\d{10}$/.test(digits);
    case 'cri':
      return digits.length >= 8 && digits.length <= 10;
    case 'fra':
      // UX: aceptar tanto formato nacional (10) como internacional sin 0 inicial (9)
      return digits.length === 9 || digits.length === 10;
    case 'gbr':
      // UK tiene longitudes variables; evitar bloqueos
      return digits.length >= 7 && digits.length <= 11;
    case 'twn':
      return digits.length >= 8 && digits.length <= 10;
    case 'deu':
      return digits.length >= 6 && digits.length <= 15;
    case 'ita':
      return digits.length >= 6 && digits.length <= 12;
    default:
      return true;
  }
}
